import { mutatorAction } from 'satcheljs';
import { assignOpenShiftsPollingState } from '../store/assignOpenShiftsPollingState';
import { AssignOpenShiftsActiveJob, UndoData } from '../store/schema/AssignOpenShiftsPollingStateSchema';
import { JobStatus } from 'sh-models/job/SmartScheduleGenerationJobStatusEntity';
import { ECSConfigKey, ECSConfigService } from 'sh-services';
export type { FailureData, UndoData, ReassignParams } from '../store/schema/AssignOpenShiftsPollingStateSchema';

export const setAssignOpenShiftsJob = mutatorAction(
    'setAssignOpenShiftsJob',
    function setAssignOpenShiftsJob(job: AssignOpenShiftsActiveJob) {
        assignOpenShiftsPollingState().activeJob = { ...job };
    }
);

export const updateAssignOpenShiftsJobStatus = mutatorAction(
    'updateAssignOpenShiftsJobStatus',
    function updateAssignOpenShiftsJobStatus(status: JobStatus) {
        if (assignOpenShiftsPollingState().activeJob) {
            assignOpenShiftsPollingState().activeJob.status = status;
        }
    }
);

export const clearAssignOpenShiftsJob = mutatorAction(
    'clearAssignOpenShiftsJob',
    function clearAssignOpenShiftsJob() {
        assignOpenShiftsPollingState().activeJob = undefined;
    }
);

export const setShowSuccessToast = mutatorAction(
    'setShowSuccessToast',
    function setShowSuccessToast(value: boolean, undoData?: UndoData, successCount?: number) {
        assignOpenShiftsPollingState().showSuccessToast = value;
        if (value) {
            assignOpenShiftsPollingState().undoData = undoData;
            assignOpenShiftsPollingState().successCount = successCount;
        } else {
            assignOpenShiftsPollingState().partialSuccessData = undefined;
            assignOpenShiftsPollingState().undoData = undefined;
            assignOpenShiftsPollingState().successCount = undefined;
        }
    }
);

export const setPartialSuccessToast = mutatorAction(
    'setPartialSuccessToast',
    function setPartialSuccessToast(assigned: number, total: number, undoData?: UndoData) {
        assignOpenShiftsPollingState().partialSuccessData = { assigned, total };
        assignOpenShiftsPollingState().undoData = undoData;
        assignOpenShiftsPollingState().showSuccessToast = true;
    }
);

export const setShowFailureToast = mutatorAction(
    'setShowFailureToast',
    function setShowFailureToast(tagId: string, teamId: string) {
        assignOpenShiftsPollingState().failureData = { tagId, teamId };
        assignOpenShiftsPollingState().showFailureToast = true;
    }
);

export const clearFailureToast = mutatorAction(
    'clearFailureToast',
    function clearFailureToast() {
        assignOpenShiftsPollingState().showFailureToast = false;
        assignOpenShiftsPollingState().failureData = undefined;
    }
);

export const setShowCancelledToast = mutatorAction(
    'setShowCancelledToast',
    function setShowCancelledToast(value: boolean) {
        assignOpenShiftsPollingState().showCancelledToast = value;
    }
);

/**
 * Clears every Assign Open Shifts **result** toast (Success / Partial / Failure / Cancelled) and
 * bumps `dismissResultToastsSignal` so the toast component can call FluentUI's `dismissToast(id)`
 * for each result toast id. Intentionally leaves `activeJob` untouched — the Assigning (in-progress)
 * toast stays sticky while polling is running. Wired to:
 *   - clicks on a `<button>` inside a surface marked with `data-toast-dismiss-trigger="true"` —
 *     currently the command bars (Schedule / Global), Add Group, and Add Members. Dialog and
 *     panel interiors are NOT marked, so their internal buttons leave the toast intact and
 *     preserve the Undo affordance (Schedules.tsx capture-phase listener);
 *   - clicks on items in the shift / member contextual menus (Add shift, Edit shift, Add time
 *     off, Copy, Paste, Delete, Share, Change color, Move to open shifts, Assign open shift,
 *     Move/Remove member, etc.). These menus render in a callout portal outside the schedules
 *     subtree, so the listener can't see them; the dispatch happens via FluentUI's `onItemClick`
 *     hook on each ContextualMenu (ShiftContextualMenu / ScheduleMemberContextualMenu);
 *   - navigation away from the schedules screen (Schedules.componentWillUnmount);
 * so the user always sees a clean schedule view after they take a major action or move past a
 * result.
 *
 * Gated on `ECSConfigKey.EnableAssignOpenShifts`. When the feature is off, the result toasts
 * are never dispatched in the first place (polling service is gated), so dismissing is moot.
 * Gating here makes every caller — command bar marker listener, context menu wrappers,
 * schedules unmount — a guaranteed no-op without each having to repeat the flag check.
 */
export const dismissAllAssignOpenShiftsResultToasts = mutatorAction(
    'dismissAllAssignOpenShiftsResultToasts',
    function dismissAllAssignOpenShiftsResultToasts() {
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts)) {
            return;
        }
        const state = assignOpenShiftsPollingState();
        state.showSuccessToast = false;
        state.partialSuccessData = undefined;
        state.undoData = undefined;
        state.successCount = undefined;
        state.showFailureToast = false;
        state.failureData = undefined;
        state.showCancelledToast = false;
        state.dismissResultToastsSignal = state.dismissResultToastsSignal + 1;
    }
);
